<template>
  <div class="filename-compositor">
    <div
      ref="compositorInput"
      class="compositor-input"
      contenteditable="true"
      :placeholder="$t('customers.settings.placeholder')"
      @input="parseContent"
      @keydown="handleKeydown"
      @paste="handlePaste"
    />
    <el-alert
      v-if="showInvalidCharsWarning"
      class="invalid-chars-warning"
      :title="$t('customers.settings.invalidCharacters')"
      type="warning"
      show-icon
      :closable="false"
    />
  </div>
</template>

<script>
export default {
  name: 'FilenameCompositor',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    tagLabels: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isParsing: false,
      showInvalidCharsWarning: false,
      invalidCharsRegex: /[/:*?"<>|]/g,
    };
  },
  watch: {
    value: {
      handler() {
        if (this.isParsing) return;
        this.renderContent();
      },
      deep: true,
    },
  },
  mounted() {
    this.renderContent();
  },
  methods: {
    renderContent() {
      const { compositorInput } = this.$refs;
      if (!compositorInput) return;

      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      let cursorNode = null;
      let cursorOffset = 0;

      if (range && compositorInput.contains(range.startContainer)) {
        cursorNode = range.startContainer;
        cursorOffset = range.startOffset;
      }

      compositorInput.innerHTML = '';

      this.value.forEach((item, index) => {
        if (item.type === 'tag') {
          const tagElement = this.createTagElement(item.value, index);
          compositorInput.appendChild(tagElement);
        } else if (item.type === 'text') {
          const textNode = document.createTextNode(item.value);
          compositorInput.appendChild(textNode);
        }
      });

      if (cursorNode) {
        try {
          const newRange = document.createRange();
          newRange.setStart(cursorNode, cursorOffset);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } catch (e) {
          compositorInput.focus();
        }
      }

      this.$nextTick(() => {
        this.ensureCursorPositions();
      });
    },
    createTagElement(tagValue, index) {
      const tagSpan = document.createElement('span');
      tagSpan.className = 'tag-item el-tag el-tag--primary el-tag--small';
      tagSpan.contentEditable = 'false';
      tagSpan.dataset.tagValue = tagValue;
      tagSpan.dataset.tagIndex = index;

      const tagLabel = document.createElement('span');
      tagLabel.textContent = this.tagLabels[tagValue] || tagValue;

      const removeBtn = document.createElement('i');
      removeBtn.className = 'el-tag__close el-icon-close';
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const tagElementToRemove = e.target.closest('.tag-item');
        if (tagElementToRemove) {
          const { compositorInput } = this.$refs;
          const allTagsInDom = Array.from(compositorInput.querySelectorAll('.tag-item'));
          const domIndex = allTagsInDom.indexOf(tagElementToRemove);

          if (domIndex > -1) {
            let tagCounter = 0;
            let valueIndex = -1;
            for (let i = 0; i < this.value.length; i += 1) {
              if (this.value[i].type === 'tag') {
                if (tagCounter === domIndex) {
                  valueIndex = i;
                  break;
                }
                tagCounter += 1;
              }
            }

            if (valueIndex > -1) {
              this.removeTag(valueIndex);
            }
          }
        }
      });

      tagSpan.appendChild(tagLabel);
      tagSpan.appendChild(removeBtn);

      return tagSpan;
    },
    handleKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        return;
      }

      if (event.key === 'Backspace') {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const { startContainer, startOffset } = range;

          if (range.collapsed && startOffset === 0 && startContainer.previousSibling && startContainer.previousSibling.classList.contains('tag-item')) {
            event.preventDefault();
            const tagIndex = parseInt(startContainer.previousSibling.dataset.tagIndex, 10);
            this.removeTag(tagIndex);
          } else if (startContainer.nodeType === Node.ELEMENT_NODE && startContainer.classList.contains('tag-item')) {
            event.preventDefault();
            const tagIndex = parseInt(startContainer.dataset.tagIndex, 10);
            this.removeTag(tagIndex);
          }
        }
      }
    },
    handlePaste(event) {
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      const sanitizedText = this.sanitizeText(text);
      document.execCommand('insertText', false, sanitizedText);
    },
    sanitizeText(text) {
      const hasInvalidChars = this.invalidCharsRegex.test(text);
      if (hasInvalidChars) {
        this.showInvalidCharsWarning = true;
        setTimeout(() => {
          this.showInvalidCharsWarning = false;
        }, 3000);
      }
      return text.replace(this.invalidCharsRegex, '');
    },
    parseContent() {
      this.isParsing = true;
      const { compositorInput } = this.$refs;
      if (!compositorInput) {
        this.isParsing = false;
        return;
      }

      const newValue = [];
      compositorInput.childNodes.forEach((currentNode) => {
        const node = currentNode;
        if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('tag-item')) {
          newValue.push({
            type: 'tag',
            value: node.dataset.tagValue,
          });
        } else if (node.nodeType === Node.TEXT_NODE) {
          const sanitizedText = this.sanitizeText(node.textContent);
          if (node.textContent !== sanitizedText) {
            node.textContent = sanitizedText;
          }
          const cleanText = sanitizedText.replace(/\u200B/g, '');
          if (cleanText) {
            newValue.push({ type: 'text', value: cleanText });
          }
        }
      });

      const mergedValue = [];
      let lastItem = null;
      newValue.forEach((item) => {
        if (lastItem && lastItem.type === 'text' && item.type === 'text') {
          lastItem.value += item.value;
        } else {
          lastItem = { ...item };
          mergedValue.push(lastItem);
        }
      });

      this.$emit('input', mergedValue);

      this.$nextTick(() => {
        this.isParsing = false;
        this.ensureCursorPositions();
      });
    },
    removeTag(index) {
      const newValue = [...this.value];
      newValue.splice(index, 1);
      this.$emit('input', newValue);
    },
    insertTag(tagValue) {
      const { compositorInput } = this.$refs;
      compositorInput.focus();

      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      range.deleteContents();

      const tagNode = this.createTagElement(tagValue, -1); // Index is temporary
      range.insertNode(tagNode);

      range.setStartAfter(tagNode);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);

      this.parseContent();
    },
    ensureCursorPositions() {
      const { compositorInput } = this.$refs;
      if (!compositorInput) return;

      const { childNodes } = compositorInput;
      const nodes = Array.from(childNodes);

      // Ensure space between tags
      for (let i = 0; i < nodes.length - 1; i += 1) {
        const currentNode = nodes[i];
        const nextNode = nodes[i + 1];
        if (
          currentNode.nodeType === Node.ELEMENT_NODE
          && currentNode.classList.contains('tag-item')
          && nextNode.nodeType === Node.ELEMENT_NODE
          && nextNode.classList.contains('tag-item')
        ) {
          const space = document.createTextNode('\u200B');
          compositorInput.insertBefore(space, nextNode);
        }
      }

      // Ensure space at the beginning
      const firstNode = compositorInput.firstChild;
      if (firstNode && firstNode.nodeType === Node.ELEMENT_NODE && firstNode.classList.contains('tag-item')) {
        const space = document.createTextNode('\u200B');
        compositorInput.insertBefore(space, firstNode);
      }

      // Ensure space at the end
      const lastNode = compositorInput.lastChild;
      if (lastNode && lastNode.nodeType === Node.ELEMENT_NODE && lastNode.classList.contains('tag-item')) {
        const space = document.createTextNode('\u200B');
        compositorInput.appendChild(space);
      }
    },
  },
};
</script>

<style lang='scss' scoped>
.compositor-input {
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  background-image: none;
  border-radius: 0.25rem;
  border: 1px solid #dcdfe6;
  box-sizing: border-box;
  color: #606266;
  display: inline-block;
  font-size: inherit;
  min-height: 2.5rem;
  line-height: 1.5;
  outline: 0;
  padding: 0.3rem 0.9375rem;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: 100%;
  cursor: text;
  white-space: pre-wrap;
  word-break: break-word;

  &:hover {
    border-color: #c0c4cc;
  }

  &:focus {
    border-color: #409eff;
  }

  &[contenteditable='true']:empty:before {
    content: attr(placeholder);
    color: #c0c4cc;
  }
}

.compositor-input ::v-deep .tag-item {
  display: inline-block;
  align-items: center;
  margin: 0.125rem 0.25rem;
  padding: 0 0.625rem;
  height: 1.5rem;
  line-height: 1.375rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  user-select: none;
  vertical-align: middle;
}

.invalid-chars-warning {
  margin-top: 0.5rem;
}
</style>
